import { Navigate, Outlet } from 'react-router';
import { useAuth } from '@/lib/auth';
import type { Permissao } from '@/tipos/api';

/**
 * Autorização por **permissão**, usada como rota-mãe:
 * `{ element: <RequerPermissao permissao="usuarios.ver" />, children: [...] }`.
 *
 * Por que permissão e não papel: o papel `admin` tem tudo, mas os demais são **configuráveis pelo
 * admin** (CLAUDE.md, §"Usuários e permissões"). Uma tela que perguntasse "é admin?" passaria a
 * mentir no dia em que um papel novo recebesse a permissão — e não existe super admin aqui.
 *
 * 🚨 Esta é a **segunda** tranca, não a primeira. A que vale é o `@Permissao()` no backend; aqui só
 * evitamos mostrar à pessoa uma tela que o servidor negaria. Começa fechado: sem a permissão na
 * lista, não passa.
 *
 * Quem não tem a permissão vai para `/`, sem mensagem de erro: ela chegou por um link que não lhe
 * serve (favorito antigo, link colado por um colega), e um alerta vermelho sugeriria que algo
 * quebrou. O item de menu correspondente também não aparece (`layout/itens-menu.ts`).
 */
export function RequerPermissao({ permissao }: { permissao: Permissao }) {
  const { autenticado, carregando, tem } = useAuth();

  // A `RotaProtegida` já cuida do intervalo de carregamento; aqui não decidimos sem saber quem é.
  if (carregando) return null;
  if (!autenticado) return <Navigate to="/entrar" replace />;
  if (!tem(permissao)) return <Navigate to="/" replace />;
  return <Outlet />;
}
